// Create this as /src/components/ui/cors-image.tsx
import { useState, useEffect } from 'react';

interface CORSImageProps {
  src: string;
  alt: string;
  className?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onLoad?: () => void;
}

export function CORSImage({ src, alt, className, onError, onLoad }: CORSImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);

  // Reset when src changes
  useEffect(() => {
    setCurrentSrc(src);
    setRetryCount(0);
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error(`🚨 Image failed to load (attempt ${retryCount + 1}):`, currentSrc);
    
    if (retryCount === 0) {
      // First retry: Try with a cache-busting parameter
      const separator = currentSrc.includes('?') ? '&' : '?';
      const cacheBustUrl = `${currentSrc}${separator}v=${Date.now()}`;
      console.log('🔄 Retrying with cache-bust:', cacheBustUrl);
      setCurrentSrc(cacheBustUrl);
      setRetryCount(1);
      return;
    }
    
    // Final fallback: Use placeholder
    console.log('🔄 Using placeholder fallback');
    setCurrentSrc("https://placehold.co/640x360/1f1f1f/ffffff?text=Image+Failed");
    
    if (onError) onError(e);
  };

  const handleLoad = () => {
    console.log('✅ Image loaded successfully:', currentSrc);
    if (onLoad) onLoad();
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={handleError}
      onLoad={handleLoad}
      // ✅ Only add CORS attributes for external URLs
      {...(currentSrc.includes('cloudinary.com') ? {
        crossOrigin: 'anonymous' as const,
        referrerPolicy: 'no-referrer' as const
      } : {})}
    />
  );
}